import type { ReactNode } from 'react';

import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Link } from '@evisort/eds/components/Link/Link';
import { Text } from '@evisort/eds/components/Text/Text';
import { defineMessages, useIntl } from 'react-intl';

import brandLockupSrc from '~/assets/images/brand/brand-color-blue-horizontal.svg';
import brandLockupDarkSrc from '~/assets/images/brand/brand-color-white-horizontal.svg';
import { TimeOfDayGradientBackground } from '~/common/brandGradient/TimeOfDayGradientBackground';
import { useTimeOfDayGradient } from '~/common/brandGradient/useTimeOfDayGradient';
import {
  BRAND_NAME,
  BRAND_PRODUCT_TAGLINE,
  BRAND_TERMS_OF_USE_LABEL,
} from '~/common/constants/brand';

import styles from './LoginLayout.module.css';

const TERMS_OF_USE_URL = 'https://www.evisort.com/terms/';

const MARKETING_IMAGE_AVIF_752 = '/images/login/login-marketing-752.avif';
const MARKETING_IMAGE_JPG_752 = '/images/login/login-marketing-752.jpg';
const MARKETING_IMAGE_WEBP_752 = '/images/login/login-marketing-752.webp';
const MARKETING_IMAGE_AVIF_1504 = '/images/login/login-marketing-1504.avif';
const MARKETING_IMAGE_JPG_1504 = '/images/login/login-marketing-1504.jpg';
const MARKETING_IMAGE_WEBP_1504 = '/images/login/login-marketing-1504.webp';

const messages = defineMessages({
  copyright: {
    id: 'LoginLayout.copyright',
    defaultMessage: '© {year} Workday, Inc. All rights reserved.',
    description: 'Footer copyright notice on the sign-in page',
  },
  logoAlt: {
    id: 'LoginLayout.logoAlt',
    defaultMessage: '{brandName} logo',
    description: 'Alt text for the Evisort brand lockup',
  },
  pitchBody: {
    id: 'LoginLayout.pitchBody',
    defaultMessage:
      'Minimize risk and drive results across procurement, finance, sales, M&A, HR, IT, and more with Workday CLM and {productTagline}.',
    description: 'Supporting copy in the sign-in page marketing panel',
  },
  pitchHeading: {
    id: 'LoginLayout.pitchHeading',
    defaultMessage: 'Make contracts a strategic advantage.',
    description: 'Heading in the sign-in page marketing panel',
  },
  termsOfUse: {
    id: 'LoginLayout.termsOfUse',
    defaultMessage: '{termsOfUseLabel}',
    description: 'Footer link to the terms of use',
  },
});

interface Props {
  children: ReactNode;
}

function BrandLockup({ className }: { className?: string }) {
  const intl = useIntl();

  return (
    <div className={className}>
      <img
        alt={intl.formatMessage(messages.logoAlt, { brandName: BRAND_NAME })}
        className={`${styles.logo} ${styles.logoLight}`}
        src={brandLockupSrc}
      />
      <img
        alt={intl.formatMessage(messages.logoAlt, { brandName: BRAND_NAME })}
        className={`${styles.logo} ${styles.logoDark}`}
        src={brandLockupDarkSrc}
      />
    </div>
  );
}

/**
 * Chrome for the sign-in route: the brand lockup over the form on the left,
 * with a decorative brand-gradient panel on the right. Only `children` — the
 * form for the current stage — varies.
 */
export function LoginLayout({ children }: Props) {
  const intl = useIntl();
  const { period } = useTimeOfDayGradient();

  return (
    <div className={styles.page} data-time-of-day={period}>
      <TimeOfDayGradientBackground period={period} />

      {/* Shown only below the split-screen breakpoint, above the card —
          `.logoLink` inside `.formPanel` is hidden there instead. */}
      <BrandLockup className={styles.mobileLogoLink} />

      <div className={styles.formPanel}>
        <BrandLockup className={styles.logoLink} />

        <div className={styles.formArea}>
          <div className={styles.form}>{children}</div>
        </div>

        <Layout
          align="center"
          as="footer"
          className={styles.footer}
          direction="column"
          spacing="xs"
        >
          <Text as="p" tone="subdued" variant="body-s">
            {intl.formatMessage(messages.copyright, {
              year: new Date().getFullYear(),
            })}
          </Text>
          <Link
            href={TERMS_OF_USE_URL}
            size="s"
            text={intl.formatMessage(messages.termsOfUse, {
              termsOfUseLabel: BRAND_TERMS_OF_USE_LABEL,
            })}
          />
        </Layout>
      </div>

      <aside className={styles.aside}>
        <picture>
          <source
            srcSet={`${MARKETING_IMAGE_AVIF_752} 752w, ${MARKETING_IMAGE_AVIF_1504} 1504w`}
            type="image/avif"
          />
          <source
            srcSet={`${MARKETING_IMAGE_WEBP_752} 752w, ${MARKETING_IMAGE_WEBP_1504} 1504w`}
            type="image/webp"
          />
          <img
            alt=""
            className={styles.marketingImage}
            decoding="async"
            fetchPriority="high"
            src={MARKETING_IMAGE_JPG_752}
            srcSet={`${MARKETING_IMAGE_JPG_752} 752w, ${MARKETING_IMAGE_JPG_1504} 1504w`}
          />
        </picture>

        <Layout className={styles.pitch} direction="column" spacing="s">
          <Text as="h2" variant="heading-m">
            {intl.formatMessage(messages.pitchHeading)}
          </Text>
          <Text as="p" variant="body-m">
            {intl.formatMessage(messages.pitchBody, {
              productTagline: BRAND_PRODUCT_TAGLINE,
            })}
          </Text>
        </Layout>
      </aside>
    </div>
  );
}
